import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';

import { BaseComponent } from '~components/abstractions/base.component';
import { BaseSearchStore } from '~core/abstractions/base-search.store';
import { Identifyable } from '~core/abstractions/identifyable';
import { ActionButtonModel } from '~core/models/action-button.model';
import { ActionButtonsService } from '~core/services/action-buttons.service';
import { TranslationService } from '~core/services/translation.service';
import { IconComponent } from '../icon/icon.component';
import { ButtonType } from './models/button.type';

@Component({
    selector: 'arc-button',
    templateUrl: './button.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [MatTooltip, MatButton, NgClass, NgTemplateOutlet, MatIconButton, MatProgressSpinner, IconComponent, MatIcon]
})
export class ButtonComponent extends BaseComponent implements OnChanges {
    @Input() type: ButtonType = '';
    @Input() color?: ThemePalette;
    @Input() spinnerColor: ThemePalette = 'primary';
    @Input() contextActionsStore?: BaseSearchStore<any>;
    @Input() customClasses = '';
    @Input() isDisabled = false;
    @Input() isLoading = false;
    @Input() actionButtonKey?: string;
    @Input() actionButtonTitleKey = '';
    @Input() currentData?: Identifyable<any>;
    @Input() tooltipText = '';
    @Input() prefixIcon?: string;

    @Output() readonly clicked = new EventEmitter<void>();

    title?: string;
    icon?: string;

    private readonly actionButtonsService = inject(ActionButtonsService);
    private readonly translationService = inject(TranslationService);

    private buttons = this.actionButtonsService.buttons;

    ngOnChanges(): void {
        if (!!this.actionButtonKey) {
            const btn = this.actionButtonsService.getButtonByKey(this.actionButtonKey);

            if (!btn && !!this.contextActionsStore) {
                this.contextActionsStore.getContextActions().subscribe(resp => {
                    const contextAction = (resp.value || []).find(cta => cta.key === this.actionButtonKey);

                    if (!contextAction) {
                        this.setupActionButton(undefined);
                        return;
                    }

                    const buttonModels = this.actionButtonsService.convertOutOfContextActions([contextAction], this.contextActionsStore!);

                    this.setupActionButton(buttonModels[0], true);
                });
            } else {
                this.setupActionButton(btn);
            }
        }
    }

    handleClick(): void {
        this.isLoading = true;

        if (!!this.actionButtonKey) {
            const sub = this.actionButtonsService.checkButtonLoadingStatusSub.subscribe(() => {
                const btn = this.buttons().find(b => b.key === this.actionButtonKey);
                this.performActionButtonClick(btn);
            });

            this.addSubscriptions(sub);
            this.actionButtonsService.handleClick(this.actionButtonKey, this.currentData, undefined, () => this.performClick());

            return;
        }

        this.performClick();
    }

    private setupActionButton(btn?: ActionButtonModel, isTranslated = false): void {
        this.title = btn?.text;
        this.icon = btn?.icon;
        this.isDisabled = !btn || this.actionButtonsService.isHidden(btn.key, this.currentData);
        this.tooltipText = this.isDisabled ? btn?.failedConditionMessage || '' : '';

        if (!isTranslated) {
            this.translationService.getTextAsync(this.actionButtonTitleKey).then(t => (this.title = t));
        }
    }

    private performActionButtonClick(btn?: ActionButtonModel): void {
        if (!!btn && !btn.isLoading) {
            this.performClick();
        }
    }

    private performClick(): void {
        // We trigger the event even if an action button is referenced.
        // That way we allow for custom post-processing.
        this.clicked.emit();
        this.isLoading = false;
    }
}
